/**
 * The price curve of the BI dashboards: inside the detail of a cell of "Season vs last year" (CU-869fmhmx8 E9) and in
 * "Price curve of a date" (CU-869fmjzrg, `buildDateCurve`), one computation for both (CA 9). Each provider's price on
 * each capture day, by days before the pick-up (`d`, from `days` down to 0). The server hands back every provider's
 * points; the lines, the market median of the providers shown (E3), Min / Avg / Max and the switches are composed here,
 * so none of them asks again.
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { addDays, daysBetween, fmtRange, fmtWeekday, fromIso, startOfDay } from './dates.js'
import { median } from './stats.js'

// fewer prices than this on a day: hollow point (as the cell's "!", CA 14); fewer days than this: "too few capture days"
export const THIN_PRICES = 5
export const FEW_DAYS = 3

// colours of the lines (UX v4, dataviz): never red or green, which mean dearer / cheaper on this dashboard
export const CURVE_COLORS = Object.freeze({ you: '#2563EB', opened: '#8B5CF6', median: '#6B7280', other: '#CBD2DC' })
export const CURVE_WIDTHS = Object.freeze({ you: 2.75, opened: 2.25, median: 2, other: 1.25 })

/** The key of the session cache: the same body → the same curve */
export const curveKey = body => JSON.stringify(body)

const pointsOf = (raw, stat) => (raw || [])
	.filter(p => p[stat] !== null && p[stat] !== undefined)
	.map(p => ({ d: p.d, value: p[stat], n: p.n || 0, thin: (p.n || 0) < THIN_PRICES, captured: p.captured || null }))

/** The market median at each distance: the median of the competitors shown that have a price that day */
function medianPoints(series) {
	const byD = new Map()
	for (const s of series) for (const p of s) {
		if (!byD.has(p.d)) byD.set(p.d, [])
		byD.get(p.d).push(p)
	}
	return [...byD.entries()].sort((a, b) => b[0] - a[0]).map(([d, ps]) => {
		const n = ps.reduce((sum, p) => sum + p.n, 0)
		return { d, value: median(ps.map(p => p.value)), n, thin: n < THIN_PRICES, captured: ps[0].captured }
	})
}

const line = (key, role, name, points, prev, extra = {}) => ({
	key, role, name, color: CURVE_COLORS[role], width: CURVE_WIDTHS[role], points, prev,
	fewDays: points.length < FEW_DAYS, ...extra,
})

/**
 * The lines of the curve (UX "Líneas por defecto"):
 *  - opened from a competitor or another own brand → You + that provider + Market median;
 *  - from your row or from Market median → You + Market median;
 *  - without identity → that provider + Market median (Market median alone when opened from it).
 * Market median and "Other competitors" are among the providers shown (`visible`, E3: a Set of ids, `null` = all).
 * `prev` (Last year only) is the same line one year before.
 */
export function buildCurve(data, { stat = 'min', visible = null, openedId = null } = {}) {
	const identity = data?.identity || null
	const hasPrev = data?.compare === 'ly' && !!data?.previousPickupDates
	const shown = visible ? new Set([...visible].map(Number)) : null
	const providers = (data?.providers || []).map(p => ({ ...p, own: identity ? p.own || null : null }))
		.filter(p => !shown || p.own === 'primary' || shown.has(Number(p.id)))
	const pts = (p, which) => pointsOf(data?.points?.[String(p.id)]?.[which], stat)

	const you = identity ? providers.find(p => p.own === 'primary') || null : null
	const competitors = providers.filter(p => !p.own)
	const opened = openedId === null || openedId === undefined ? null : providers.find(p => Number(p.id) === Number(openedId)) || null

	const main = []
	if (you) main.push(line(`p${you.id}`, 'you', you.name, pts(you, 'cur'), hasPrev ? pts(you, 'prev') : null, { tag: 'You', providerId: you.id }))
	if (opened && opened !== you) {
		main.push(line(`p${opened.id}`, 'opened', opened.name, pts(opened, 'cur'), hasPrev ? pts(opened, 'prev') : null,
			{ tag: opened.own === 'other' ? 'Yours' : null, providerId: opened.id }))
	}
	main.push(line('median', 'median', 'Market median',
		medianPoints(competitors.map(p => pts(p, 'cur'))),
		hasPrev ? medianPoints(competitors.map(p => pts(p, 'prev'))) : null))

	const others = competitors.filter(p => !opened || Number(p.id) !== Number(opened.id))
		.map(p => line(`p${p.id}`, 'other', p.name, pts(p, 'cur'), null, { providerId: p.id }))

	const all = [...main, ...others]
	return {
		days: data?.days || 0, compare: data?.compare || 'ly', hasPrev, main, others,
		empty: !all.some(s => s.points.length),
		single: (data?.pickupDates || []).length === 1,
		pickupDates: data?.pickupDates || [], lastCapture: data?.lastCapture || null,
	}
}

/**
 * Last week / Yesterday have no curve to compare with (it is this one, cut earlier): a vertical mark at the distance
 * of the comparison, `null` when it falls outside the axis. By week, the first pick-up.
 */
export function compareMarker({ compare, pickupDates = [], days, today }) {
	if (compare !== 'lw' && compare !== 'yday') return null
	const pickup = fromIso(pickupDates[0])
	if (!pickup) return null
	const yesterday = addDays(startOfDay(today), -1)
	const d = daysBetween(addDays(yesterday, compare === 'lw' ? -7 : -1), pickup)
	if (d < 0 || d > days) return null
	return { d, label: `compared with: ${compare === 'lw' ? 'a week before' : 'the day before'}` }
}

/** A future pick-up: from yesterday to the pick-up nothing is captured yet (by week, the first pick-up) */
export function notCapturedZone({ pickupDates = [], days, today }) {
	const pickup = fromIso(pickupDates[0])
	if (!pickup) return null
	const yesterday = addDays(startOfDay(today), -1)
	const from = daysBetween(yesterday, pickup)
	if (from <= 0) return null
	return { from: Math.min(from, days), to: 0, label: 'not captured yet' }
}

/** ← → move the cursor one day on the inverted axis (left = further from the pick-up); Home / End to the ends */
export function nextCursorDay(d, key, days) {
	const at = d === null || d === undefined ? 0 : d
	if (key === 'ArrowLeft') return Math.min(days, d === null || d === undefined ? 0 : at + 1)
	if (key === 'ArrowRight') return Math.max(0, at - 1)
	if (key === 'Home') return days
	if (key === 'End') return 0
	return d ?? null
}

/**
 * `xKind="pass"` of `CurveChart` (CU-869fn0gcp phase 2, DS Repricing › Last changes): x = the passes in time order,
 * 0 … `last`, left to right. ← → step one pass, Home / End jump to the first / last; the first press lands on an end.
 */
export function nextCursorPass(i, key, last) {
	const max = Math.max(0, last || 0)
	if (i === null || i === undefined) {
		if (key === 'ArrowRight' || key === 'Home') return 0
		if (key === 'ArrowLeft' || key === 'End') return max
		return null
	}
	if (key === 'ArrowRight') return Math.min(max, i + 1)
	if (key === 'ArrowLeft') return Math.max(0, i - 1)
	if (key === 'Home') return 0
	if (key === 'End') return max
	return i
}

/** The x axis text of `xKind="pass"`: the pass label at a whole index, nothing between passes or outside them. */
export function passAxisLabel(text, labels) {
	const n = Number(text)
	if (!Array.isArray(labels) || !Number.isInteger(n) || n < 0 || n >= labels.length) return ''
	return labels[n] || ''
}

const pointAt = (points, d) => (points || []).find(p => p.d === d) || null

/**
 * What the tooltip (and the screen reader, through an `aria-live` region) says at a distance: the title, when it was
 * captured and one row per line shown — `{ name, value, ly, thin }`, `value` null = "No prices that day".
 */
export function curveTooltip(curve, d, { hidden = {}, showPrev = false, showOthers = false } = {}) {
	const lines = [...curve.main, ...(showOthers ? curve.others : [])].filter(s => !hidden[s.key])
	const first = lines.map(s => pointAt(s.points, d)).find(Boolean)
	const captured = curve.single
		? (first?.captured ? `captured ${fmtWeekday(fromIso(first.captured))}` : null)
		: `captured ${d} day${d === 1 ? '' : 's'} before each pick-up`
	return {
		title: d === 0 ? 'Pick-up day' : `${d} day${d === 1 ? '' : 's'} before pick-up`,
		captured,
		rows: lines.map(s => {
			const p = pointAt(s.points, d)
			const ly = showPrev && s.prev ? pointAt(s.prev, d) : null
			return { key: s.key, name: s.name, tag: s.tag || null, color: s.color, value: p ? p.value : null, thin: !!p?.thin, ly: ly ? ly.value : null }
		}),
	}
}

/** The notes under the chart */
export function curveFoot(curve, { hidden = {}, showPrev = false } = {}) {
	const shown = curve.main.filter(s => !hidden[s.key])
	const anyThin = shown.some(s => s.points.some(p => p.thin) || (showPrev && s.prev?.some(p => p.thin)))
	const sparse = shown.filter(s => s.fewDays).map(s => s.name)
	return {
		thin: anyThin ? '○ Hollow points: fewer than 5 prices that day.' : null,
		sparse: sparse.length ? `${sparse.join(', ')}: too few capture days for a curve.` : null,
		gaps: 'Lines join the days with prices; a gap means no capture that day.',
	}
}

/** The subtitle of the section: what each point is */
export function curveSubtitle({ pickupDates = [], stat = 'min', lor }) {
	const dates = pickupDates.map(fromIso).filter(Boolean)
	const word = { min: 'Min', avg: 'Avg', max: 'Max' }[stat] || 'Min'
	if (dates.length === 1) return `pick-up ${fmtWeekday(dates[0])}: ${word} per provider on each capture day · ${lor}-day rentals`
	if (!dates.length) return ''
	return `pick-ups ${fmtRange(dates[0], dates[dates.length - 1])}: ${word} of the week's pick-ups at each distance · ${lor}-day rentals`
}

export const noCurveText = days => `No prices captured for this pick-up in the ${days} days before it.`

// "Price curve of a date" (CU-869fmjzrg): one line per provider. Nine named tones, never red or green (they mean dearer
// / cheaper), assigned by the provider's place in the answer, so a line keeps its colour when Tier / Providers change.
// The same hexes are `dataColors.series1…9` (`src/Theme/palette.js`).
export const SERIES_COLORS = Object.freeze(['#7C3AED', '#CA8A04', '#0891B2', '#C026D3', '#EA580C', '#57534E', '#78350F', '#1F2937', '#DB2777'])
export const DATE_CURVE_WIDTHS = Object.freeze({ you: 3, median: 2.4, other: 1.6 })

/**
 * The lines of "Price curve of a date" (CU-869fmjzrg D-A): **the same points and the same median as the cell curve**
 * (`pointsOf`, `medianPoints`; CA 9), with every provider shown as a line. You and Market median first, then the
 * competitors and your other brands (marked `Yours`) by name. `visible` = Tier / Providers (a Set of ids, `null` = all):
 * the median is among the visible competitors; your brand always stays.
 */
export function buildDateCurve(data, { stat = 'min', visible = null } = {}) {
	const identity = data?.identity || null
	const hasPrev = data?.compare === 'ly' && !!data?.previousPickupDates
	const shown = visible ? new Set([...visible].map(Number)) : null
	const all = (data?.providers || []).map(p => ({ ...p, own: identity ? p.own || null : null }))
	const colorOf = new Map(all.filter(p => p.own !== 'primary').map((p, i) => [Number(p.id), SERIES_COLORS[i % SERIES_COLORS.length]]))
	const providers = all.filter(p => !shown || p.own === 'primary' || shown.has(Number(p.id)))
	const pts = (p, which) => pointsOf(data?.points?.[String(p.id)]?.[which], stat)
	const mk = (key, role, name, points, prev, extra) => ({
		key, role, name, points, prev, color: extra.color, width: DATE_CURVE_WIDTHS[role], fewDays: points.length < FEW_DAYS, ...extra,
	})

	const youP = identity ? providers.find(p => p.own === 'primary') || null : null
	const competitors = providers.filter(p => !p.own)
	const you = youP ? mk(`p${youP.id}`, 'you', youP.name, pts(youP, 'cur'), hasPrev ? pts(youP, 'prev') : null,
		{ color: CURVE_COLORS.you, tag: 'You', providerId: Number(youP.id) }) : null
	const median = mk('median', 'median', 'Market median', medianPoints(competitors.map(p => pts(p, 'cur'))),
		hasPrev ? medianPoints(competitors.map(p => pts(p, 'prev'))) : null, { color: CURVE_COLORS.median, competitors: competitors.length })
	const others = providers.filter(p => p !== youP).sort((a, b) => String(a.name).localeCompare(String(b.name)))
		.map(p => mk(`p${p.id}`, 'other', p.name, pts(p, 'cur'), hasPrev ? pts(p, 'prev') : null,
			{ color: colorOf.get(Number(p.id)), tag: p.own === 'other' ? 'Yours' : null, providerId: Number(p.id), own: p.own || null }))

	const lines = [...(you ? [you] : []), median, ...others]
	return {
		days: data?.days || 0, pick: data?.pick || 'date', hasPrev, you, median, others, lines,
		empty: !lines.some(l => l.points.length),
		single: (data?.pickupDates || []).length === 1,
		pickupDates: data?.pickupDates || [], lastCapture: data?.lastCapture || null,
		beyond: !!data?.beyond, startsOn: data?.startsOn || null, window: data?.window || null,
		identity, competitors: competitors.length, total: all.filter(p => p.own !== 'primary').length,
	}
}

export const LABEL_GAP = 13
const LABEL_FONT_PX = 6.6 // ≈ width of a character at 11 px, bold included

/** The width of the label column: the longest name, whole, never cut (`min`…`max`, then the name is shortened with …) */
export function labelColumnWidth(names, { min = 84, max = 180, pad = 12 } = {}) {
	const longest = Math.max(0, ...names.map(n => String(n || '').length))
	return Math.max(min, Math.min(max, Math.ceil(longest * LABEL_FONT_PX) + pad))
}

/**
 * The labels of the line ends (CU-869fmjzrg CA 7, as the approved mock): in a column **to the right of the plot**
 * (beyond "pick-up", so no line — last year's dotted included — and no shaded zone runs under them), each at the height
 * of its line's last point, pushed apart at least `gap` and kept between `top` and `bottom`, with a leader line from the
 * point to the label. `ends` = `[{ key, name, x, y }]` in px of the chart; `plotRight` = the plot's right edge.
 * `{ x, width, labels: [{ key, name, y, fromX, fromY }] }`.
 */
export function labelLayout({ ends, plotRight, top, bottom, gap = LABEL_GAP, margin = 8 }) {
	// never more labels than fit in the plot's height: keep the first ones by `priority` (You, the median, the highlighted
	// line, then the order given), the rest stay unlabelled — their line and the legend still name them
	const capacity = Math.max(1, Math.floor((bottom - top) / gap) + 1)
	const kept = ends.length > capacity
		? [...ends].map((e, i) => ({ e, p: e.priority ?? i + 10 })).sort((a, b) => a.p - b.p).slice(0, capacity).map(x => x.e)
		: ends
	const sorted = [...kept].sort((a, b) => a.y - b.y)
	const ys = []
	sorted.forEach((e, i) => ys.push(i ? Math.max(e.y, ys[i - 1] + gap) : Math.max(e.y, top)))
	const over = ys.length ? ys[ys.length - 1] - bottom : 0
	if (over > 0) for (let i = 0; i < ys.length; i++) ys[i] = Math.max(top + i * gap, ys[i] - over)
	const width = labelColumnWidth(sorted.map(e => e.name))
	return {
		x: plotRight + margin, width,
		labels: sorted.map((e, i) => ({ key: e.key, name: e.name, y: ys[i], fromX: e.x, fromY: e.y })),
	}
}

/** @deprecated by `labelLayout` (kept for the label spacing test): `{ key: y }` pushed at least `gap` apart */
export function labelOffsets(items, { gap = LABEL_GAP } = {}) {
	const out = {}
	let last = -Infinity
	for (const it of [...items].sort((a, b) => a.y - b.y)) {
		const y = Math.max(it.y, last + gap)
		out[it.key] = y
		last = y
	}
	return out
}

/**
 * The bottom of the y axis (Curro's review): prices are never negative, so the axis starts at a round value at or
 * under the lowest price shown, and never below 0. `values` = every value drawn.
 */
export function axisFloor(values) {
	const xs = (values || []).filter(v => typeof v === 'number' && Number.isFinite(v))
	if (!xs.length) return 0
	const lo = Math.min(...xs), hi = Math.max(...xs)
	const span = hi - lo || Math.abs(hi) || 1
	const raw = span / 5
	const pow = 10 ** Math.floor(Math.log10(raw))
	const step = [1, 2, 5, 10].map(m => m * pow).find(x => x >= raw)
	return Math.max(0, Math.floor((lo - span * 0.05) / step) * step)
}

export const DEFAULT_LINES = 5

/**
 * The lines shown when a date curve opens (Curro's review: 40 lines cannot be read). You, Market median and the
 * `n` competitors **nearest to your price** on the last capture day; without your brand (or without your price), the
 * `n` cheapest. Every other line starts off and can be turned on from the legend. A Set of line keys.
 */
export function defaultShown(curve, n = DEFAULT_LINES) {
	if (!curve) return new Set()
	const lastValue = l => [...(l.points || [])].sort((a, b) => a.d - b.d)[0]?.value ?? null
	const shown = new Set([curve.median?.key, curve.you?.key].filter(Boolean))
	const others = (curve.others || []).map(l => ({ l, v: lastValue(l) })).filter(x => x.v !== null)
	const you = curve.you ? lastValue(curve.you) : null
	const order = you === null
		? others.sort((a, b) => a.v - b.v || String(a.l.name).localeCompare(String(b.l.name)))
		: others.sort((a, b) => Math.abs(a.v - you) - Math.abs(b.v - you) || a.v - b.v)
	order.slice(0, n).forEach(x => shown.add(x.l.key))
	return shown
}

/**
 * Which lines are on, and the legend's order, for one curve (QA): every new curve — another pick-up, office, cars or
 * statistic — opens with its default lines (`defaultShown`), unless the user already turned chips on or off for the
 * same answer (`dataId`; changing only the statistic keeps those choices). `first` is the legend's leading group, the
 * one the defaults drew, so the legend order and greying always match what is drawn.
 * `{ dataId, stat, hidden: { key: true }, first: [key], touched }`.
 */
export function linesState(prev, curve, { dataId = null, stat = null } = {}) {
	if (prev && prev.touched && prev.dataId === dataId) return prev.stat === stat ? prev : { ...prev, stat }
	if (prev && !prev.touched && prev.dataId === dataId && prev.stat === stat && prev.curve === curve) return prev
	const first = defaultShown(curve)
	return {
		dataId, stat, curve, touched: false, first: [...first],
		hidden: Object.fromEntries((curve?.others || []).filter(l => !first.has(l.key)).map(l => [l.key, true])),
	}
}

/** The user turns lines on or off: `hidden` is theirs for this answer from now on */
export const touchLines = (state, hidden) => ({ ...state, hidden, touched: true })
